import type { ThirdPartyResponsesRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetThirdPartyResponsesByThirdPartySubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetThirdPartyResponsesByThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

/**
 * A respondent submitting in the portal flips the response to
 * `awaiting_review` — an EXTERNAL actor, so client-side invalidation cannot
 * observe it and the V3 stack has no subscription transport. 5s matches the
 * other focused feeds (`useGetChangeRequestsByParentSubscription`): this list
 * is scoped to one third party, and the reviewer acts on rows from it.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Holding-pattern subscription hook for a third party's questionnaire responses
 * (COI-581) — backs the Questionnaires tab.
 *
 * - `trpc` ON  → polls `frontend.thirdPartyResponse.byThirdPartyId`.
 * - `trpc` OFF → the legacy Hasura `getThirdPartyResponsesByThirdParty`
 *   subscription.
 */
export const useGetThirdPartyResponsesByThirdPartySubscription =
  createSubscriptionHook<
    { thirdPartyId: string },
    ThirdPartyResponsesRegisterResponseRow[],
    GetThirdPartyResponsesByThirdPartySubscription
  >({
    trpcQueryOptions: (trpc, { thirdPartyId }) =>
      trpc.frontend.thirdPartyResponse.byThirdPartyId.queryOptions({
        thirdPartyId,
      }),
    trpcRefetchInterval: () => POLL_INTERVAL_MS,
    mapTrpcDataToGraphQL: (rows) => ({
      third_party_response: rows,
    }),
    graphqlDocument: GetThirdPartyResponsesByThirdPartyDocument,
    graphqlVariables: ({ thirdPartyId }) => ({ ThirdPartyId: thirdPartyId }),
  });
